import React, { useState } from 'react';
import { 
  X, 
  Share2, 
  Copy, 
  Check, 
  Link as LinkIcon, 
  Clock, 
  Mail, 
  Send,
  Loader2,
  Calendar,
  Layers,
  Globe,
  MessageSquare
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import axios from 'axios';
import { User } from 'firebase/auth';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandId: string;
  planId?: string;
  ideaIds?: string[];
  type: 'plan' | 'post';
  user: User;
}

import { doc, setDoc, getDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { v4 as uuidv4 } from 'uuid';

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  brandId,
  planId,
  ideaIds,
  type,
  user
}) => {
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [expiresDays, setExpiresDays] = useState(7);

  const handleGenerate = async () => {
    const emails = clientEmail.split(',').map(e => e.trim()).filter(e => e);
    if (emails.length === 0) {
      alert("Please enter the client's email address(es) to send the review link.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const em of emails) {
      if (!emailRegex.test(em)) {
        alert(`Please enter a valid email address. "${em}" is invalid.`);
        return;
      }
    }

    setLoading(true);
    try {
      // 1. Fetch Plan & Brand directly in frontend
      console.log("Fetching brand...");
      const brandSnap = await getDoc(doc(db, 'brands', brandId)).catch(err => {
        console.error("Brand fetch error:", err);
        throw err;
      });
      if (!brandSnap.exists()) throw new Error("Brand not found");
      const brandData = brandSnap.data();

      let content: any[] = [];
      let planData: any = null;

      if (planId) {
        console.log("Fetching plan...");
        const planSnap = await getDoc(doc(db, 'plans', planId)).catch(err => {
          console.error("Plan fetch error:", err);
          throw err;
        });
        if (planSnap.exists()) {
          planData = planSnap.data();
          let rawCalendar = planData.calendar;
          if (typeof rawCalendar === 'string') {
            try { rawCalendar = JSON.parse(rawCalendar); } catch(e) { rawCalendar = []; }
          }
          if (!Array.isArray(rawCalendar)) rawCalendar = [];

          if (ideaIds && ideaIds.length > 0) {
            const targetIds = ideaIds.map(id => String(id));
            content = rawCalendar.filter((idea: any) => targetIds.includes(String(idea.id)));
          } else {
            content = rawCalendar;
          }
        }
      }

      console.log("Creating shared link via API...");
      const idToken = await user.getIdToken();
      const response = await axios.post('/api/share/create', {
        brandId,
        planId: planId || null,
        ideaIds: ideaIds || null,
        expiresDays,
        clientEmail,
        message,
        type
      }, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      setShareUrl(response.data.url);

    } catch (err: any) {
      console.error(err);
      const errorMsg = err.response?.data?.error || err.message || "Failed to generate share link";
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            <div className="p-8">
              <header className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center text-indigo-600 ring-4 ring-indigo-50">
                    <Share2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Share for Approval</h2>
                    <p className="text-sm text-gray-500">Generate a live link for your client</p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </header>

              {!shareUrl ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-4 rounded-2xl border-2 transition-all ${type === 'plan' ? 'border-indigo-500 bg-indigo-50/50' : 'border-gray-100 cursor-not-allowed opacity-50'}`}>
                       <div className="flex items-center justify-between mb-2">
                          <Calendar className={`w-5 h-5 ${type === 'plan' ? 'text-indigo-600' : 'text-gray-400'}`} />
                          {type === 'plan' && <Check className="w-4 h-4 text-indigo-600" />}
                       </div>
                       <p className={`font-bold text-sm ${type === 'plan' ? 'text-indigo-900' : 'text-gray-400'}`}>Full Plan</p>
                       <p className="text-[10px] text-gray-500">All posts in the calendar</p>
                    </div>
                    <div className={`p-4 rounded-2xl border-2 transition-all ${type === 'post' ? 'border-indigo-500 bg-indigo-50/50' : 'border-gray-100 cursor-not-allowed opacity-50'}`}>
                       <div className="flex items-center justify-between mb-2">
                          <Layers className={`w-5 h-5 ${type === 'post' ? 'text-indigo-600' : 'text-gray-400'}`} />
                          {type === 'post' && <Check className="w-4 h-4 text-indigo-600" />}
                       </div>
                       <p className={`font-bold text-sm ${type === 'post' ? 'text-indigo-900' : 'text-gray-400'}`}>Specific Posts</p>
                       <p className="text-[10px] text-gray-500">{ideaIds?.length || 0} selected</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                      <Mail className="w-3 h-3" /> Send to Client Email
                    </label>
                    <input
                      type="text"
                      required
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      placeholder="client1@company.com, client2@company.com"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-gray-900 placeholder:text-gray-400"
                      title="You can enter multiple emails separated by commas."
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                      <MessageSquare className="w-3 h-3" /> Custom Message (Optional)
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Add a personal note..."
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all resize-none text-gray-900 placeholder:text-gray-400"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                      <Clock className="w-3 h-3" /> Link Expiry
                    </label>
                    <select
                      value={expiresDays}
                      onChange={(e) => setExpiresDays(Number(e.target.value))}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none text-gray-900"
                    >
                      <option value={1} className="text-gray-900">24 Hours</option>
                      <option value={3} className="text-gray-900">3 Days</option>
                      <option value={7} className="text-gray-900">7 Days</option>
                      <option value={14} className="text-gray-900">14 Days</option>
                      <option value={30} className="text-gray-900">30 Days</option>
                    </select>
                  </div>

                  <button
                    disabled={loading}
                    onClick={handleGenerate}
                    className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-500 shadow-xl shadow-indigo-600/20 transition-all disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    Generate Secure Link
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-100 rounded-2xl p-6 text-center">
                    <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg shadow-green-500/20">
                      <Check className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-green-900">Link Generated!</h3>
                    <p className="text-sm text-green-600">Your client can now view and approve content.</p>
                  </div>

                  <div className="relative">
                    <input
                      readOnly
                      value={shareUrl}
                      className="w-full px-4 py-4 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 font-mono focus:outline-none"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-gray-100 shadow-sm"
                    >
                      {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-gray-400" />}
                    </button>
                  </div>

                  <p className="text-[10px] text-gray-400 text-center">
                    This link will expire on {new Date(Date.now() + expiresDays * 86400000).toLocaleDateString()}.
                  </p>

                  <button
                    onClick={() => setShareUrl(null)}
                    className="w-full py-3 text-sm font-bold text-gray-500 hover:text-indigo-600 transition-colors"
                  >
                    Generate another link
                  </button>
                </div>
              )}
            </div>
            
            <div className="bg-gray-50 px-8 py-6 border-t border-gray-100 flex items-center justify-between">
              <p className="text-[10px] text-gray-500 leading-tight">
                Users don't need a PlataPost account to view the content. <br/>
                All approvals and comments will appear directly in your dashboard.
              </p>
              <Globe className="w-8 h-8 text-gray-200" />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
